import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import $ from 'jquery';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import * as action from '../../Action/CuratedCollection/CuratedCollection_Action';
import {
    MAX_SERIES,
    MIN_ACTIVE_SERIES,
    NAME_MAX_LENGTH,
    validateCuratedCollection,
} from '../../utils/curatedCollectionValidation';
import { usePermission } from '../../hooks/usePermission';
import { PERM } from '../../config/permissions';
import Model from '../Model/Index';

/**
 * Curated Collection — the "Trending" rail that sits under the hero banner on the
 * app home page. One global, hand-ordered list, shown to every user.
 *
 * Backed by three calls on the curation resource:
 *   GET /curation/curated-collection               → { name, is_active, items[] }
 *   GET /curation/curated-collection/series-search → active series to pick from
 *   PUT /curation/curated-collection               → { name, is_active, series_ids }
 *
 * The config row is a singleton, so PUT is a full replace rather than a create:
 * `series_ids` IS the display order. Everything on this screen — renaming,
 * adding, removing, reordering, toggling the rail on or off — stays local until
 * Save fires that one request. That is what the PRD means by "changes take
 * effect only after the admin explicitly saves".
 *
 * Series that go inactive are dropped by the backend, so the list that comes
 * back from GET can be shorter than the one that was saved.
 */

const DEFAULT_NAME = 'Trending';

// The server caps `limit` at 50; "Load more" pages past that.
const SEARCH_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 350;

/** Unwraps the shared response envelope: { success, message, data, ... } → data. */
const unwrap = (res) => (res?.data !== undefined ? res.data : res?.responseDetails);

const readMessage = (res, fallback) => res?.responseMessage || res?.message || fallback;

/** A saved row: `id` is the row id, `series_id` is what PUT wants back. */
const fromConfigItem = (item) => ({
    seriesId: Number(item?.series_id),
    title: item?.series_title || '',
    thumbnail: item?.series_thumbnail || '',
});

/** A search hit: here `id` IS the series id. */
const fromSearchResult = (row) => ({
    seriesId: Number(row?.id),
    title: row?.title || '',
    thumbnail: row?.thumbnail || '',
});

const byPosition = (a, b) => Number(a?.position ?? 0) - Number(b?.position ?? 0);

const sameSeries = (a, b) =>
    a.length === b.length && a.every((item, i) => item.seriesId === b[i].seriesId);

const SortableSeriesRow = ({
    item,
    index,
    total,
    positionValue,
    onPositionChange,
    onPositionCommit,
    onRemove,
    allowEdit,
    busy,
}) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
        useSortable({ id: item.seriesId, disabled: !allowEdit || busy });

    return (
        <div
            ref={setNodeRef}
            className={`cc-row${isDragging ? ' is-dragging' : ''}`}
            style={{ transform: CSS.Transform.toString(transform), transition }}
        >
            <button
                type="button"
                className="cc-handle"
                title={allowEdit ? 'Drag to reorder' : 'Reordering needs edit permission'}
                disabled={!allowEdit || busy}
                {...attributes}
                {...listeners}
            >
                <i className="fas fa-grip-vertical"></i>
            </button>

            <span className="cc-position-badge">#{index + 1}</span>

            <span className="cc-thumb">
                <i className="fas fa-film"></i>
                {item.thumbnail && (
                    <img
                        src={item.thumbnail}
                        alt=""
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                )}
            </span>

            <span className="cc-title" title={item.title}>
                {item.title || `Series #${item.seriesId}`}
            </span>

            {allowEdit && (
                <div className="cc-position-set">
                    <label htmlFor={`cc-position-${item.seriesId}`}>Position</label>
                    <input
                        id={`cc-position-${item.seriesId}`}
                        type="number"
                        min="1"
                        max={total}
                        value={positionValue}
                        disabled={busy}
                        onChange={(e) => onPositionChange(item.seriesId, e.target.value)}
                        onBlur={() => onPositionCommit(item, index)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                onPositionCommit(item, index);
                            }
                        }}
                    />
                </div>
            )}

            {allowEdit && (
                <button
                    type="button"
                    className="cc-remove"
                    title="Remove from the collection"
                    disabled={busy}
                    onClick={() => onRemove(item)}
                >
                    <i className="fas fa-times"></i>
                </button>
            )}
        </div>
    );
};

// `onNameChange` lets the tab strip on /curated label this tab with the saved name.
const CuratedCollection = ({ onNameChange }) => {
    const dispatch = useDispatch();
    const { canEdit } = usePermission();
    const allowEdit = canEdit(PERM.SERIES);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Working copy. Everything here is local until Save.
    const [name, setName] = useState(DEFAULT_NAME);
    const [isActive, setIsActive] = useState(false);
    const [items, setItems] = useState([]);
    const [positionDrafts, setPositionDrafts] = useState({});

    // Last-saved snapshot, for the dirty check and Reset.
    const [baseline, setBaseline] = useState({ name: DEFAULT_NAME, isActive: false, items: [] });
    const [meta, setMeta] = useState({ updatedAt: null, updatedBy: null });

    const [showPicker, setShowPicker] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);
    const [searchPage, setSearchPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    // An array, not a Set: the order the admin ticks boxes is the order the
    // series land in the list.
    const [selectedIds, setSelectedIds] = useState([]);

    const applyConfig = useCallback((config) => {
        const nextName = config?.name || DEFAULT_NAME;
        const nextActive = config?.is_active === true || config?.is_active === 1;
        const nextItems = (Array.isArray(config?.items) ? config.items : [])
            .slice()
            .sort(byPosition)
            .map(fromConfigItem)
            .filter((item) => Number.isFinite(item.seriesId));

        setName(nextName);
        setIsActive(nextActive);
        setItems(nextItems);
        setBaseline({ name: nextName, isActive: nextActive, items: nextItems });
        setPositionDrafts({});
        setMeta({ updatedAt: config?.updated_at || null, updatedBy: config?.updated_by || null });
    }, []);

    const fetchConfig = useCallback(() => {
        setLoading(true);
        return dispatch(action.getCuratedCollection())
            .then((res) => applyConfig(unwrap(res)))
            .catch((err) => {
                toast.error(readMessage(err, 'Failed to load the curated collection.'));
            })
            .finally(() => setLoading(false));
    }, [dispatch, applyConfig]);

    useEffect(() => { fetchConfig(); }, [fetchConfig]);

    useEffect(() => { onNameChange?.(baseline.name); }, [baseline.name, onNameChange]);

    const trimmedName = name.trim();

    const dirty = useMemo(() => (
        trimmedName !== baseline.name.trim()
        || isActive !== baseline.isActive
        || !sameSeries(items, baseline.items)
    ), [trimmedName, isActive, items, baseline]);

    // The first entry doubles as the toast if Save is attempted anyway.
    const errors = useMemo(() => validateCuratedCollection({
        name: trimmedName,
        isActive,
        seriesIds: items.map((i) => i.seriesId),
    }), [trimmedName, isActive, items]);

    const canSave = allowEdit && dirty && errors.length === 0 && !saving;

    // The whole screen is one unsaved form; closing the tab loses the lot.
    useEffect(() => {
        if (!dirty) return undefined;
        const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [dirty]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        setItems((prev) => {
            const from = prev.findIndex((i) => i.seriesId === active.id);
            const to = prev.findIndex((i) => i.seriesId === over.id);
            if (from === -1 || to === -1) return prev;
            return arrayMove(prev, from, to);
        });
    };

    const onPositionChange = (seriesId, value) =>
        setPositionDrafts((prev) => ({ ...prev, [seriesId]: value }));

    const onPositionCommit = (item, index) => {
        const raw = positionDrafts[item.seriesId];
        setPositionDrafts((prev) => {
            const next = { ...prev };
            delete next[item.seriesId];
            return next;
        });
        if (raw === undefined || String(raw).trim() === '') return;
        const parsed = parseInt(raw, 10);
        if (Number.isNaN(parsed)) return;
        const target = Math.min(Math.max(parsed, 1), items.length) - 1;
        if (target === index) return;
        setItems((prev) => arrayMove(prev, index, target));
    };

    const removeSeries = (item) => {
        Swal.fire({
            title: `Remove "${item.title || `Series #${item.seriesId}`}"?`,
            text: 'It only leaves this collection — the series itself is untouched. Positions below it shift up.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: 'var(--first-color)',
            cancelButtonColor: 'var(--error-color)',
            confirmButtonText: 'Yes, remove it',
            customClass: { confirmButton: 'btn', cancelButton: 'btn' },
        }).then((result) => {
            if (!result.isConfirmed) return;
            setItems((prev) => prev.filter((i) => i.seriesId !== item.seriesId));
        });
    };

    const resetChanges = () => {
        setName(baseline.name);
        setIsActive(baseline.isActive);
        setItems(baseline.items);
        setPositionDrafts({});
    };

    const handleSave = () => {
        if (!allowEdit) {
            toast.error("Opps! You don't have Permission.");
            return;
        }
        if (errors.length > 0) {
            toast.error(errors[0]);
            return;
        }

        setSaving(true);
        dispatch(action.saveCuratedCollection({
            name: trimmedName,
            is_active: isActive,
            series_ids: items.map((i) => i.seriesId),
        }))
            .then((res) => {
                toast.success(readMessage(res, 'Curated collection saved.'));
                // PUT echoes the stored config, which is the authority on what
                // actually persisted (inactive series may have been dropped).
                const saved = unwrap(res);
                if (saved && typeof saved === 'object') {
                    applyConfig(saved);
                } else {
                    fetchConfig();
                }
            })
            .catch((err) => {
                const details = Array.isArray(err?.details) && err.details.length > 0
                    ? err.details.map((d) => d?.message || d).join('; ')
                    : null;
                toast.error(details || readMessage(err, 'Failed to save the curated collection.'));
            })
            .finally(() => setSaving(false));
    };

    // ─── Picker ──────────────────────────────────────────────────────────────

    // Guards against a slow early search overwriting a later one's results.
    const searchSeq = useRef(0);

    const runSearch = useCallback((term, page, append) => {
        const seq = searchSeq.current + 1;
        searchSeq.current = seq;

        if (append) setLoadingMore(true); else setSearchLoading(true);

        const params = { page, limit: SEARCH_PAGE_SIZE };
        if (term.trim()) params.search = term.trim();

        dispatch(action.searchCuratedCollectionSeries(params))
            .then((res) => {
                if (seq !== searchSeq.current) return;
                const raw = unwrap(res);
                const rows = (Array.isArray(raw) ? raw : [])
                    .map(fromSearchResult)
                    .filter((row) => Number.isFinite(row.seriesId));
                setResults((prev) => {
                    if (!append) return rows;
                    const seen = new Set(prev.map((r) => r.seriesId));
                    return [...prev, ...rows.filter((r) => !seen.has(r.seriesId))];
                });
                setSearchPage(page);
                setHasMore(rows.length === SEARCH_PAGE_SIZE);
            })
            .catch((err) => {
                if (seq !== searchSeq.current) return;
                toast.error(readMessage(err, 'Failed to search series.'));
                if (!append) setResults([]);
                setHasMore(false);
            })
            .finally(() => {
                if (seq !== searchSeq.current) return;
                setSearchLoading(false);
                setLoadingMore(false);
            });
    }, [dispatch]);

    // Runs on open (an empty term lists every active series) and per keystroke.
    useEffect(() => {
        if (!showPicker) return undefined;
        const handle = setTimeout(() => runSearch(searchTerm, 1, false), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [showPicker, searchTerm, runSearch]);

    const openPicker = () => {
        if (!allowEdit) {
            toast.error("Opps! You don't have Permission.");
            return;
        }
        if (items.length >= MAX_SERIES) {
            toast.error(`All ${MAX_SERIES} slots are filled. Remove a series before adding another.`);
            return;
        }
        setShowPicker(true);
        setSearchTerm('');
        setResults([]);
        setSelectedIds([]);
        setSearchPage(1);
        setHasMore(false);
        $('body').addClass('no_scroll');
        $('.overlay').addClass('qv_active');
    };

    const closePicker = useCallback(() => {
        setShowPicker(false);
        setSelectedIds([]);
        setSearchTerm('');
        setResults([]);
        $('body').removeClass('no_scroll');
        $('.overlay').removeClass('qv_active');
    }, []);

    // A route change while the modal is open would otherwise strand these.
    useEffect(() => () => {
        $('body').removeClass('no_scroll');
        $('.overlay').removeClass('qv_active');
    }, []);

    const existingIds = useMemo(() => new Set(items.map((i) => i.seriesId)), [items]);
    const remainingSlots = MAX_SERIES - items.length;
    const slotsLeftAfterSelection = remainingSlots - selectedIds.length;

    const toggleSelected = (seriesId) => {
        setSelectedIds((prev) => {
            if (prev.includes(seriesId)) return prev.filter((x) => x !== seriesId);
            if (prev.length >= remainingSlots) {
                toast.error(
                    `Only ${remainingSlots} slot${remainingSlots === 1 ? '' : 's'} left in the ${MAX_SERIES}-series list.`,
                );
                return prev;
            }
            return [...prev, seriesId];
        });
    };

    const addSelected = () => {
        if (selectedIds.length === 0) {
            toast.error('Select at least one series.');
            return;
        }
        const byId = new Map(results.map((r) => [r.seriesId, r]));
        // selectedIds is in tick order, so the additions keep that order.
        const chosen = selectedIds
            .map((id) => byId.get(id))
            .filter((row) => row && !existingIds.has(row.seriesId));

        if (chosen.length === 0) {
            toast.error('Those series are already in the collection.');
            return;
        }

        setItems((prev) => [...prev, ...chosen].slice(0, MAX_SERIES));
        toast.success(`${chosen.length} series added. Save to publish the change.`);
        closePicker();
    };

    const filledLabel = `${items.length} of ${MAX_SERIES} slot${items.length === 1 ? '' : 's'} filled`;

    return (
        <>
            <div className="cc-page">
                <div className="cc-toolbar">
                    <div className="cc-intro">
                        <h5>Ordered Collection</h5>
                        <p>
                            The curated rail directly below the hero banner on the home page.
                            Every user sees this list, in the order below. Drag the
                            <i className="fas fa-grip-vertical mx-1"></i>
                            handle or type a position, then save to publish.
                        </p>
                    </div>

                    {allowEdit && (
                        <div className="cc-actions">
                            {dirty && (
                                <button
                                    type="button"
                                    className="btn cc-btn-ghost"
                                    onClick={resetChanges}
                                    disabled={saving}
                                >
                                    <i className="fas fa-undo me-2"></i>Reset
                                </button>
                            )}
                            <button
                                type="button"
                                className="btn cc-btn-ghost"
                                onClick={openPicker}
                                disabled={saving || items.length >= MAX_SERIES}
                                title={items.length >= MAX_SERIES ? `All ${MAX_SERIES} slots are filled` : undefined}
                            >
                                <i className="fas fa-plus me-2"></i>Add Series
                            </button>
                            <button
                                type="button"
                                className="btn upgrade-btn"
                                onClick={handleSave}
                                disabled={!canSave}
                            >
                                {saving
                                    ? <><i className="fas fa-spinner fa-spin me-2"></i>Saving...</>
                                    : <><i className="fas fa-save me-2"></i>Save &amp; Publish</>}
                            </button>
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border" style={{ color: 'var(--first-color)' }} role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="cc-config">
                            <div className="cc-field">
                                <label htmlFor="cc-name">Collection Name</label>
                                <input
                                    id="cc-name"
                                    type="text"
                                    maxLength={NAME_MAX_LENGTH}
                                    value={name}
                                    disabled={!allowEdit || saving}
                                    placeholder={DEFAULT_NAME}
                                    onChange={(e) => setName(e.target.value)}
                                />
                                <small>
                                    Shown as the rail heading in the app. {trimmedName.length}/{NAME_MAX_LENGTH}
                                </small>
                            </div>

                            <div className="cc-field cc-field-toggle">
                                <label htmlFor="cc-active">Active</label>
                                <div className="cc-toggle-line">
                                    <label className="cc-switch">
                                        <input
                                            id="cc-active"
                                            type="checkbox"
                                            checked={isActive}
                                            disabled={!allowEdit || saving}
                                            onChange={(e) => setIsActive(e.target.checked)}
                                        />
                                        <span className="cc-slider"></span>
                                    </label>
                                    <span className={`cc-state ${isActive ? 'is-on' : ''}`}>
                                        {isActive ? 'Enabled' : 'Disabled'}
                                    </span>
                                </div>
                                <small>
                                    Disabling hides the rail but keeps this list and its order intact.
                                </small>
                            </div>

                            <div className="cc-field cc-field-count">
                                <label>Slots</label>
                                <div className="cc-slots">
                                    <strong>{items.length}</strong>
                                    <span>/ {MAX_SERIES}</span>
                                </div>
                                <div className="cc-slot-track">
                                    {Array.from({ length: MAX_SERIES }, (_, i) => (
                                        <span
                                            key={i}
                                            className={`cc-slot-pip${i < items.length ? ' is-filled' : ''}${i < MIN_ACTIVE_SERIES ? ' is-required' : ''}`}
                                        />
                                    ))}
                                </div>
                                <small>{filledLabel} · minimum {MIN_ACTIVE_SERIES} to go live</small>
                            </div>
                        </div>

                        {errors.length > 0 && (
                            <div className="cc-note cc-note-error">
                                <i className="fas fa-circle-exclamation me-2"></i>
                                <div>
                                    {errors.map((message) => <div key={message}>{message}</div>)}
                                </div>
                            </div>
                        )}

                        {dirty && errors.length === 0 && (
                            <div className="cc-note">
                                <i className="fas fa-circle-exclamation me-2"></i>
                                Unsaved changes — click <strong className="mx-1">Save &amp; Publish</strong>
                                to make them live.
                            </div>
                        )}

                        {!isActive && items.length >= MIN_ACTIVE_SERIES && !dirty && (
                            <div className="cc-note cc-note-muted">
                                <i className="fas fa-eye-slash me-2"></i>
                                The collection is disabled, so it is not showing on the home page.
                            </div>
                        )}

                        {items.length === 0 ? (
                            <div className="cc-empty">
                                <i className="fas fa-fire"></i>
                                <h5>No series selected yet</h5>
                                <p>
                                    {allowEdit
                                        ? `Click "Add Series" to pick up to ${MAX_SERIES} series and set the order users will see.`
                                        : 'Nothing has been added to this collection.'}
                                </p>
                            </div>
                        ) : (
                            <div className="cc-list">
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleDragEnd}
                                >
                                    <SortableContext
                                        items={items.map((i) => i.seriesId)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        {items.map((item, index) => (
                                            <SortableSeriesRow
                                                key={item.seriesId}
                                                item={item}
                                                index={index}
                                                total={items.length}
                                                positionValue={
                                                    positionDrafts[item.seriesId] !== undefined
                                                        ? positionDrafts[item.seriesId]
                                                        : String(index + 1)
                                                }
                                                onPositionChange={onPositionChange}
                                                onPositionCommit={onPositionCommit}
                                                onRemove={removeSeries}
                                                allowEdit={allowEdit}
                                                busy={saving}
                                            />
                                        ))}
                                    </SortableContext>
                                </DndContext>
                            </div>
                        )}

                        {meta.updatedAt && (
                            <p className="cc-meta">
                                Last saved {new Date(meta.updatedAt).toLocaleString()}
                                {meta.updatedBy ? ` by admin #${meta.updatedBy}` : ''}.
                                A series that goes inactive is dropped from this list automatically.
                            </p>
                        )}
                    </>
                )}
            </div>

            {showPicker && (
                <Model className="common-popup add-series-popup active">
                    <div className="edit-heading d-flex align-items-center justify-content-between">
                        <h3>Add Series to {trimmedName || DEFAULT_NAME}</h3>
                        <button className="common-close edit-close-btn" onClick={closePicker}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="18" viewBox="0 0 20 18">
                                <path fill="#ffffff"
                                    d="M19.95 16.75l-.05-.4-1.2-1-5.2-4.2c-.1-.05-.3-.2-.6-.5l-.7-.55c-.15-.1-.5-.45-1-1.1l-.1-.1c.2-.15.4-.35.6-.55l1.95-1.85 1.1-1c1-1 1.7-1.65 2.1-1.9l.5-.35c.4-.25.65-.45.75-.45.2-.15.45-.35.65-.6s.3-.5.3-.7l-.3-.65c-.55.2-1.2.65-2.05 1.35-.85.75-1.65 1.55-2.5 2.5-.8.9-1.6 1.65-2.4 2.3-.8.65-1.4.95-1.9 1-.15 0-1.5-1.05-4.1-3.2C3.1 2.6 1.45 1.2.7.55L.45.1c-.1.05-.2.15-.3.3C.05.55 0 .7 0 .85l.05.35.05.4 1.2 1 5.2 4.15c.1.05.3.2.6.5l.7.6c.15.1.5.45 1 1.1l.1.1c-.2.15-.4.35-.6.55l-1.95 1.85-1.1 1c-1 1-1.7 1.65-2.1 1.9l-.5.35c-.4.25-.65.45-.75.45-.25.15-.45.35-.65.6-.15.3-.25.55-.25.75l.3.65c.55-.2 1.2-.65 2.05-1.35.85-.75 1.65-1.55 2.5-2.5.8-.9 1.6-1.65 2.4-2.3.8-.65 1.4-.95 1.9-1 .15 0 1.5 1.05 4.1 3.2 2.6 2.15 4.3 3.55 5.05 4.2l.2.45c.1-.05.2-.15.3-.3.1-.15.15-.3.15-.45z">
                                </path>
                            </svg>
                        </button>
                    </div>
                    <div className="edit-inner">
                        <p className="cc-picker-hint">
                            Only active series can be added. Picks are appended in the order you
                            tick them — reorder afterwards by dragging.
                            <strong className="d-block mt-1">
                                {remainingSlots} slot{remainingSlots === 1 ? '' : 's'} left.
                            </strong>
                        </p>

                        <div className="form-group">
                            <input
                                type="text"
                                placeholder="Search series by title..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <div className="cc-picker-list">
                            {searchLoading ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border" style={{ color: 'var(--first-color)' }} role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : results.length === 0 ? (
                                <p className="cc-picker-empty">
                                    {searchTerm.trim()
                                        ? 'No active series matches that search.'
                                        : 'No active series available.'}
                                </p>
                            ) : (
                                <>
                                    {results.map((row) => {
                                        const already = existingIds.has(row.seriesId);
                                        const checked = selectedIds.includes(row.seriesId);
                                        const blocked = !already && !checked && slotsLeftAfterSelection <= 0;
                                        return (
                                            <label
                                                className={`cc-picker-item${already || blocked ? ' is-disabled' : ''}`}
                                                key={row.seriesId}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    disabled={already || blocked}
                                                    onChange={() => toggleSelected(row.seriesId)}
                                                />
                                                <span className="cc-thumb cc-thumb-sm">
                                                    <i className="fas fa-film"></i>
                                                    {row.thumbnail && (
                                                        <img
                                                            src={row.thumbnail}
                                                            alt=""
                                                            loading="lazy"
                                                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                        />
                                                    )}
                                                </span>
                                                <span className="cc-picker-title">{row.title}</span>
                                                {already && <span className="cc-picker-badge">Already added</span>}
                                                {!already && checked && (
                                                    <span className="cc-picker-badge is-picked">
                                                        #{items.length + selectedIds.indexOf(row.seriesId) + 1}
                                                    </span>
                                                )}
                                            </label>
                                        );
                                    })}

                                    {hasMore && (
                                        <button
                                            type="button"
                                            className="cc-load-more"
                                            onClick={() => runSearch(searchTerm, searchPage + 1, true)}
                                            disabled={loadingMore}
                                        >
                                            {loadingMore ? 'Loading...' : 'Load more series'}
                                        </button>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="action-footer d-flex align-items-center justify-content-between">
                            <span className="cc-picker-count">
                                {selectedIds.length} selected · {Math.max(slotsLeftAfterSelection, 0)} slot
                                {slotsLeftAfterSelection === 1 ? '' : 's'} left
                            </span>
                            <button
                                type="button"
                                className="btn"
                                onClick={addSelected}
                                disabled={selectedIds.length === 0}
                            >
                                Add to list
                            </button>
                        </div>
                    </div>
                </Model>
            )}

            <CuratedCollectionStyles />
        </>
    );
};

export const CuratedCollectionStyles = () => (
    <style>{`
        .cc-toolbar {
            display: flex; align-items: flex-start; justify-content: space-between;
            gap: 16px; flex-wrap: wrap; margin-bottom: 18px;
        }
        .cc-intro h5 { margin: 0 0 4px; font-size: 17px; color: var(--body-text-color); }
        .cc-intro p { margin: 0; font-size: 13px; color: var(--text-color); max-width: 640px; }
        .cc-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .cc-btn-ghost {
            background: transparent; border: 1px solid var(--border-color);
            color: var(--body-text-color); font-weight: 500; font-size: 14px;
            padding: 8px 18px; border-radius: var(--radius-sm);
        }
        .cc-btn-ghost:hover:not(:disabled) { border-color: var(--first-color); color: var(--first-color); }
        .cc-btn-ghost:disabled { opacity: 0.5; }

        .cc-config {
            display: grid; grid-template-columns: minmax(220px, 2fr) minmax(180px, 1fr) minmax(180px, 1fr);
            gap: 18px; margin-bottom: 18px; padding: 18px;
            background: var(--glass-bg); border: 1px solid var(--border-color);
            border-radius: var(--radius-md);
        }
        .cc-field { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
        .cc-field > label {
            margin: 0; font-size: 11px; text-transform: uppercase;
            letter-spacing: 0.05em; color: var(--text-color); font-weight: 600;
        }
        .cc-field input[type="text"] {
            width: 100%; padding: 9px 12px; font-size: 14px;
            background: var(--body-bg-color); color: var(--body-text-color);
            border: 1px solid var(--border-color); border-radius: var(--radius-sm);
        }
        .cc-field input[type="text"]:focus {
            outline: none; border-color: var(--first-color);
            box-shadow: 0 0 0 3px rgba(var(--first-color-rgb), 0.15);
        }
        .cc-field small { font-size: 11.5px; color: var(--text-color); }

        .cc-toggle-line { display: flex; align-items: center; gap: 10px; padding: 4px 0; }
        .cc-switch { position: relative; display: inline-block; width: 42px; height: 22px; margin: 0; flex: none; }
        .cc-switch input { opacity: 0; width: 0; height: 0; }
        .cc-slider {
            position: absolute; inset: 0; cursor: pointer; border-radius: 22px;
            background: var(--body-bg-color); border: 1px solid var(--border-color);
            transition: 0.25s;
        }
        .cc-slider:before {
            position: absolute; content: ""; height: 14px; width: 14px; left: 3px; bottom: 3px;
            background: var(--text-color); border-radius: 50%; transition: 0.25s;
        }
        .cc-switch input:checked + .cc-slider {
            background: rgba(var(--first-color-rgb), 0.2); border-color: var(--first-color);
        }
        .cc-switch input:checked + .cc-slider:before {
            transform: translateX(20px); background: var(--first-color);
        }
        .cc-switch input:disabled + .cc-slider { opacity: 0.5; cursor: not-allowed; }
        .cc-state { font-size: 13px; font-weight: 600; color: var(--text-color); }
        .cc-state.is-on { color: var(--first-color); }

        .cc-slots { display: flex; align-items: baseline; gap: 4px; }
        .cc-slots strong { font-size: 24px; line-height: 1; color: var(--first-color); }
        .cc-slots span { font-size: 14px; color: var(--text-color); }
        .cc-slot-track { display: flex; gap: 4px; }
        .cc-slot-pip { flex: 1 1 0; height: 6px; border-radius: 3px; background: var(--border-color); }
        .cc-slot-pip.is-required { background: rgba(247, 64, 50, 0.25); }
        .cc-slot-pip.is-filled { background: var(--first-color); }

        .cc-note {
            display: flex; align-items: center; font-size: 13px;
            color: var(--price-color); background: rgba(255, 145, 57, 0.1);
            border: 1px solid rgba(255, 145, 57, 0.25);
            border-radius: var(--radius-sm); padding: 8px 14px; margin-bottom: 14px;
        }
        .cc-note-error {
            align-items: flex-start; color: var(--error-color);
            background: rgba(247, 64, 50, 0.1); border-color: rgba(247, 64, 50, 0.25);
        }
        .cc-note-muted {
            color: var(--text-color); background: var(--glass-bg); border-color: var(--border-color);
        }

        .cc-list { display: flex; flex-direction: column; gap: 10px; max-width: 820px; }
        .cc-row {
            display: flex; align-items: center; gap: 14px;
            background: var(--glass-bg); border: 1px solid var(--border-color);
            border-radius: var(--radius-md); padding: 10px 14px;
        }
        .cc-row.is-dragging {
            border-color: var(--first-color); box-shadow: var(--shadow-glow);
            z-index: 2; position: relative;
        }
        .cc-handle {
            background: transparent; border: none; padding: 4px 2px;
            color: var(--text-color); font-size: 16px; cursor: grab; touch-action: none;
        }
        .cc-handle:active { cursor: grabbing; }
        .cc-handle:disabled { cursor: not-allowed; opacity: 0.4; }
        .cc-position-badge {
            min-width: 42px; text-align: center; font-weight: 700; font-size: 13px;
            color: var(--first-color); background: rgba(var(--first-color-rgb), 0.12);
            border-radius: var(--radius-sm); padding: 4px 8px; flex: none;
        }
        .cc-thumb {
            position: relative; flex: none; width: 46px; height: 62px;
            border-radius: var(--radius-sm); overflow: hidden;
            background: var(--body-bg-color); border: 1px solid var(--border-color);
            display: flex; align-items: center; justify-content: center;
            color: var(--text-color); font-size: 14px;
        }
        .cc-thumb img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
        .cc-thumb-sm { width: 34px; height: 46px; font-size: 11px; }
        .cc-title {
            flex: 1 1 auto; min-width: 0; font-weight: 500; font-size: 15px;
            color: var(--body-text-color);
            overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .cc-position-set { display: flex; align-items: center; gap: 6px; flex: none; }
        .cc-position-set label {
            margin: 0; font-size: 11px; text-transform: uppercase;
            letter-spacing: 0.05em; color: var(--text-color);
        }
        .cc-position-set input {
            width: 62px; padding: 5px 8px; font-size: 13px; text-align: center;
            background: var(--body-bg-color); color: var(--body-text-color);
            border: 1px solid var(--border-color); border-radius: var(--radius-sm);
        }
        .cc-remove {
            flex: none; background: transparent; border: 1px solid var(--border-color);
            color: var(--text-color); width: 30px; height: 30px; border-radius: 50%;
            display: flex; align-items: center; justify-content: center; font-size: 12px;
        }
        .cc-remove:hover:not(:disabled) { border-color: var(--error-color); color: var(--error-color); }

        .cc-empty {
            text-align: center; padding: 48px 20px; color: var(--text-color);
            border: 1px dashed var(--border-color); border-radius: var(--radius-md);
        }
        .cc-empty i { font-size: 32px; color: var(--first-color); opacity: 0.5; }
        .cc-empty h5 { margin: 14px 0 6px; font-size: 17px; color: var(--body-text-color); }
        .cc-empty p { margin: 0; font-size: 13px; }
        .cc-meta { margin: 16px 0 0; font-size: 12px; color: var(--text-color); }

        .cc-picker-hint { font-size: 12.5px; color: var(--text-color); margin-bottom: 12px; }
        .cc-picker-list {
            max-height: 340px; overflow-y: auto; margin-bottom: 16px;
            border: 1px solid var(--border-color); border-radius: var(--radius-sm); padding: 6px;
        }
        .cc-picker-item {
            display: flex; align-items: center; gap: 10px; width: 100%;
            padding: 6px 10px; border-radius: var(--radius-sm); cursor: pointer;
            font-size: 14px; color: var(--body-text-color); margin: 0;
        }
        .cc-picker-item:hover:not(.is-disabled) { background: var(--sidebar-hover); }
        .cc-picker-item.is-disabled { opacity: 0.5; cursor: not-allowed; }
        .cc-picker-item input { width: 16px; height: 16px; accent-color: var(--first-color); flex: none; }
        .cc-picker-title {
            flex: 1 1 auto; min-width: 0;
            overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .cc-picker-badge {
            flex: none; font-size: 11px; padding: 2px 8px; border-radius: 10px;
            background: var(--body-bg-color); border: 1px solid var(--border-color);
            color: var(--text-color);
        }
        .cc-picker-badge.is-picked {
            color: var(--first-color); border-color: var(--first-color);
            background: rgba(var(--first-color-rgb), 0.12); font-weight: 700;
        }
        .cc-picker-empty { font-size: 13px; color: var(--text-color); text-align: center; padding: 24px 0; margin: 0; }
        .cc-picker-count { font-size: 13px; color: var(--text-color); }
        .cc-load-more {
            display: block; width: 100%; margin-top: 6px; padding: 8px;
            background: transparent; border: 1px dashed var(--border-color);
            border-radius: var(--radius-sm); color: var(--text-color); font-size: 13px;
        }
        .cc-load-more:hover:not(:disabled) { border-color: var(--first-color); color: var(--first-color); }

        @media (max-width: 991px) { .cc-config { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 575px) {
            .cc-config { grid-template-columns: 1fr; }
            .cc-row { flex-wrap: wrap; gap: 10px; }
            .cc-title { flex-basis: 100%; order: 4; }
        }
    `}</style>
);

export default CuratedCollection;
